/**
 * Settore — Gallery coverflow.
 *
 * Coverflow custom su GSAP (nessuna dipendenza slider esterna):
 *  - horizontalLoop() : loop infinito + centro attivo + drag/throw (Draggable +
 *    InertiaPlugin) + snap + onChange(index).
 *  - Observer         : navigazione via wheel/trackpad a intento orizzontale
 *    (niente scroll-jacking del wheel verticale).
 *  - styleByDistance  : scala/opacità/z-index continui in base alla distanza dal
 *    centro (transform-only, GPU), attivo solo quando la sezione è in viewport.
 *  - PhotoSwipe       : lightbox al click su qualsiasi slide (didascalia media).
 *
 * Stato unico = indice attivo del timeline; da lì si sincronizzano didascalia,
 * ARIA e stile. La barra rossa è statica (CSS). Rispetta prefers-reduced-motion.
 *
 * Markup: template-parts/settore/gallery.php
 */
import { gsap } from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { InertiaPlugin } from 'gsap/InertiaPlugin';
import { Observer } from 'gsap/Observer';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';

gsap.registerPlugin( Draggable, InertiaPlugin, Observer );

/**
 * Helper ufficiale GSAP "horizontalLoop" (seamless loop + draggable + center +
 * onChange). https://gsap.com/docs/v3/HelperFunctions/helpers/seamlessLoop/
 */
function horizontalLoop( items, config ) {
	let timeline;
	items = gsap.utils.toArray( items );
	config = config || {};
	gsap.context( () => {
		const onChange = config.onChange;
		let lastIndex = 0;
		const tl = gsap.timeline( {
			repeat: config.repeat,
			onUpdate: onChange && function () {
				const i = tl.closestIndex();
				if ( lastIndex !== i ) {
					lastIndex = i;
					onChange( items[ i ], i );
				}
			},
			paused: config.paused,
			defaults: { ease: 'none' },
			onReverseComplete: () => tl.totalTime( tl.rawTime() + tl.duration() * 100 ),
		} );
		const length = items.length;
		const startX = items[ 0 ].offsetLeft;
		const times = [];
		const widths = [];
		const spaceBefore = [];
		const xPercents = [];
		let curIndex = 0;
		let indexIsDirty = false;
		const center = config.center;
		const pixelsPerSecond = ( config.speed || 1 ) * 100;
		const snap = config.snap === false ? ( v ) => v : gsap.utils.snap( config.snap || 1 );
		let timeOffset = 0;
		const container = center === true ? items[ 0 ].parentNode : gsap.utils.toArray( center )[ 0 ] || items[ 0 ].parentNode;
		let totalWidth;
		const getTotalWidth = () => items[ length - 1 ].offsetLeft + xPercents[ length - 1 ] / 100 * widths[ length - 1 ] - startX + spaceBefore[ 0 ] + items[ length - 1 ].offsetWidth * gsap.getProperty( items[ length - 1 ], 'scaleX' ) + ( parseFloat( config.paddingRight ) || 0 );
		const populateWidths = () => {
			let b1 = container.getBoundingClientRect();
			let b2;
			items.forEach( ( el, i ) => {
				widths[ i ] = parseFloat( gsap.getProperty( el, 'width', 'px' ) );
				xPercents[ i ] = snap( parseFloat( gsap.getProperty( el, 'x', 'px' ) ) / widths[ i ] * 100 + gsap.getProperty( el, 'xPercent' ) );
				b2 = el.getBoundingClientRect();
				spaceBefore[ i ] = b2.left - ( i ? b1.right : b1.left );
				b1 = b2;
			} );
			gsap.set( items, { xPercent: ( i ) => xPercents[ i ] } );
			totalWidth = getTotalWidth();
		};
		let timeWrap;
		const populateOffsets = () => {
			timeOffset = center ? tl.duration() * ( container.offsetWidth / 2 ) / totalWidth : 0;
			center && times.forEach( ( t, i ) => {
				times[ i ] = timeWrap( tl.labels[ 'label' + i ] + tl.duration() * widths[ i ] / 2 / totalWidth - timeOffset );
			} );
		};
		const getClosest = ( values, value, wrap ) => {
			let i = values.length;
			let closest = 1e10;
			let index = 0;
			let d;
			while ( i-- ) {
				d = Math.abs( values[ i ] - value );
				if ( d > wrap / 2 ) {
					d = wrap - d;
				}
				if ( d < closest ) {
					closest = d;
					index = i;
				}
			}
			return index;
		};
		const populateTimeline = () => {
			let i;
			let item;
			let curX;
			let distanceToStart;
			let distanceToLoop;
			tl.clear();
			for ( i = 0; i < length; i++ ) {
				item = items[ i ];
				curX = xPercents[ i ] / 100 * widths[ i ];
				distanceToStart = item.offsetLeft + curX - startX + spaceBefore[ 0 ];
				distanceToLoop = distanceToStart + widths[ i ] * gsap.getProperty( item, 'scaleX' );
				tl.to( item, { xPercent: snap( ( curX - distanceToLoop ) / widths[ i ] * 100 ), duration: distanceToLoop / pixelsPerSecond }, 0 )
					.fromTo( item, { xPercent: snap( ( curX - distanceToLoop + totalWidth ) / widths[ i ] * 100 ) }, { xPercent: xPercents[ i ], duration: ( curX - distanceToLoop + totalWidth - curX ) / pixelsPerSecond, immediateRender: false }, distanceToLoop / pixelsPerSecond )
					.add( 'label' + i, distanceToStart / pixelsPerSecond );
				times[ i ] = distanceToStart / pixelsPerSecond;
			}
			timeWrap = gsap.utils.wrap( 0, tl.duration() );
		};
		const refresh = ( deep ) => {
			const progress = tl.progress();
			tl.progress( 0, true );
			populateWidths();
			deep && populateTimeline();
			populateOffsets();
			deep && tl.draggable && tl.paused() ? tl.time( times[ curIndex ], true ) : tl.progress( progress, true );
		};
		const onResize = () => refresh( true );
		let proxy;
		gsap.set( items, { x: 0 } );
		populateWidths();
		populateTimeline();
		populateOffsets();
		window.addEventListener( 'resize', onResize );
		function toIndex( index, vars ) {
			vars = vars || {};
			( Math.abs( index - curIndex ) > length / 2 ) && ( index += index > curIndex ? -length : length );
			const newIndex = gsap.utils.wrap( 0, length, index );
			let time = times[ newIndex ];
			if ( time > tl.time() !== index > curIndex && index !== curIndex ) {
				time += tl.duration() * ( index > curIndex ? 1 : -1 );
			}
			if ( time < 0 || time > tl.duration() ) {
				vars.modifiers = { time: timeWrap };
			}
			curIndex = newIndex;
			vars.overwrite = true;
			gsap.killTweensOf( proxy );
			return vars.duration === 0 ? tl.time( timeWrap( time ) ) : tl.tweenTo( time, vars );
		}
		tl.toIndex = ( index, vars ) => toIndex( index, vars );
		tl.closestIndex = ( setCurrent ) => {
			const index = getClosest( times, tl.time(), tl.duration() );
			if ( setCurrent ) {
				curIndex = index;
				indexIsDirty = false;
			}
			return index;
		};
		tl.current = () => ( indexIsDirty ? tl.closestIndex( true ) : curIndex );
		tl.next = ( vars ) => toIndex( tl.current() + 1, vars );
		tl.previous = ( vars ) => toIndex( tl.current() - 1, vars );
		tl.times = times;
		tl.progress( 1, true ).progress( 0, true );
		if ( config.reversed ) {
			tl.vars.onReverseComplete();
			tl.reverse();
		}
		if ( config.draggable && typeof Draggable === 'function' ) {
			proxy = document.createElement( 'div' );
			const wrap = gsap.utils.wrap( 0, 1 );
			let ratio;
			let startProgress;
			let draggable;
			let lastSnap;
			let initChangeX;
			const align = () => tl.progress( wrap( startProgress + ( draggable.startX - draggable.x ) * ratio ) );
			const syncIndex = () => tl.closestIndex( true );
			draggable = Draggable.create( proxy, {
				trigger: items[ 0 ].parentNode,
				type: 'x',
				onPressInit() {
					const x = this.x;
					gsap.killTweensOf( tl );
					tl.pause();
					startProgress = tl.progress();
					refresh();
					ratio = 1 / totalWidth;
					initChangeX = ( startProgress / -ratio ) - x;
					gsap.set( proxy, { x: startProgress / -ratio } );
				},
				onDrag: align,
				onThrowUpdate: align,
				overshootTolerance: 0,
				inertia: true,
				snap( value ) {
					if ( Math.abs( startProgress / -ratio - this.x ) < 10 ) {
						return lastSnap + initChangeX;
					}
					const time = -( value * ratio ) * tl.duration();
					const wrappedTime = timeWrap( time );
					const snapTime = times[ getClosest( times, wrappedTime, tl.duration() ) ];
					let dif = snapTime - wrappedTime;
					Math.abs( dif ) > tl.duration() / 2 && ( dif += dif < 0 ? tl.duration() : -tl.duration() );
					lastSnap = ( time + dif ) / tl.duration() / -ratio;
					return lastSnap;
				},
				onRelease() {
					syncIndex();
					draggable.isThrowing && ( indexIsDirty = true );
				},
				onThrowComplete: syncIndex,
			} )[ 0 ];
			tl.draggable = draggable;
		}
		tl.closestIndex( true );
		lastIndex = curIndex;
		onChange && onChange( items[ curIndex ], curIndex );
		timeline = tl;
		return () => window.removeEventListener( 'resize', onResize );
	} );
	return timeline;
}

( function () {
	'use strict';

	const root = document.querySelector( '[data-settore-gallery]' );
	if ( ! root ) {
		return;
	}

	const track = root.querySelector( '[data-gallery-track]' );
	const slides = gsap.utils.toArray( track ? track.children : [] );
	if ( ! slides.length ) {
		return;
	}

	const captionEl = root.querySelector( '[data-gallery-caption]' );
	const viewport = root.querySelector( '.settore-gallery__viewport' );
	const reduce = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	root.classList.add( 'is-enhanced' );
	// >3 foto → coverflow compatto (card ravvicinate/sovrapposte).
	if ( slides.length > 3 ) {
		root.classList.add( 'settore-gallery--compact' );
	}

	// Stato del rullo introduttivo (auto-roll → assestamento → nav circolare).
	let introTween = null;
	let introRunning = false;
	let introDone = false;

	// --- Coverflow loop -----------------------------------------------------
	const loop = horizontalLoop( slides, {
		paused: true,
		center: true,
		draggable: ! reduce,
		onChange: syncActive,
	} );

	function syncActive( el ) {
		slides.forEach( ( s ) => {
			s.classList.remove( 'is-active' );
			s.removeAttribute( 'aria-current' );
		} );
		el.classList.add( 'is-active' );
		el.setAttribute( 'aria-current', 'true' );
		// Durante il rullo la didascalia NON insegue le card (eviterei un flicker
		// di testo): si aggiorna solo all'assestamento e nella nav normale.
		if ( captionEl && ! introRunning ) {
			captionEl.textContent = el.getAttribute( 'data-caption' ) || '';
		}
		if ( reduce ) {
			styleByDistance();
		}
	}

	// --- Profondità 3D: scala/rotazione/translateZ per distanza dal centro --
	// Trasformo il LINK interno (il <li> resta pulito per le misure del loop).
	//
	// Chiave del layout: la distanza dal centro è normalizzata sul PITCH (larghezza
	// card + gap), non sulla mezza viewport. Così `offset` è espresso in "numero di
	// card": il vicino immediato è sempre ~±1, indipendentemente da quanto sono
	// impacchettate → ogni card ha una firma scala/profondità/rotazione netta.
	//
	// Su questo `offset` costruiamo la coverflow:
	//  • scale   : 1.12 (centro) → 0.88 (±1) → 0.70 (≥±2), decadimento a tappe.
	//  • z       : iperbolico −depth·a/(a+ease) → layering progressivo ANCHE tra
	//              le laterali (non un piano unico), curva morbida non schiacciata.
	//  • rotationY: le card "guardano" verso il centro; satura oltre l'adiacente.
	//  • x        : spacing dinamico su un ARCO tanh contenuto — respiro netto tra
	//              centro e vicini, poi compressione ai bordi. L'arco è limitato
	//              (`spread-max`), così il cluster resta centrato e compatto senza
	//              allargarsi a tutta la sezione.
	//  • opacity  : le card oltre la banda visibile si dissolvono a 0 (invece di
	//              restare "appese" a mezza distanza), così solo ~5 card sono a
	//              schermo e nessun elemento resta isolato ai lati.
	// z-index esplicito = base − |offset| e pointer-events spenti quando invisibili:
	// il centro resta sopra e gli hit-test cadono solo sulle porzioni visibili.
	const cards = slides.map( ( s ) => s.querySelector( '.settore-gallery__link' ) || s );

	// Parametri geometrici: sorgente unica in _settore-gallery.scss (CSS vars),
	// letti a runtime così la coverflow si regola dal CSS senza toccare il JS.
	const cfg = {};
	let pitch = 0; // distanza tra i centri di due card adiacenti (px)
	let cardW = 0; // larghezza card (px)
	// Direzione di scorrimento (+1 = next/verso destra, -1 = prev), dedotta dal
	// tempo del loop frame-per-frame: serve a spingere indietro SOLO la card in
	// uscita durante una transizione (vedi styleByDistance).
	let travelDir = 0;
	let lastLoopTime = null;

	function readVars() {
		const cs = getComputedStyle( root );
		const num = ( name, fallback ) => {
			const v = parseFloat( cs.getPropertyValue( name ) );
			return Number.isFinite( v ) ? v : fallback;
		};
		cfg.activeScale = num( '--active-scale', 1.12 );
		cfg.neighborScale = num( '--neighbor-scale', 0.88 );
		cfg.outerScale = num( '--outer-scale', 0.7 );
		cfg.angle = num( '--rotation-angle', 33 );
		cfg.depth = num( '--depth-z', 240 );
		cfg.depthEase = num( '--depth-ease', 1.1 );
		cfg.cardStep = num( '--card-step', 0.52 );
		cfg.fadeStart = num( '--fade-start', 4.8 );
		cfg.fadeRange = num( '--fade-range', 1.5 );
		// Loop seamless con POCHE foto: nel giro, ogni card raggiunge al massimo
		// |offset| ≈ N/2 prima di riavvolgersi (wrap) dall'altro lato. Se a quel
		// punto è ancora visibile, il wrap "sfarfalla". Serve quindi che la
		// dissolvenza finisca (opacity 0) prima della giunzione:
		//   fadeStart + fadeRange ≤ N/2.
		// Abbassiamo fade-start quanto basta; se non basta (set minuscoli, 2-3 foto)
		// restringiamo anche fade-range. Sui set ampi i valori autorali restano
		// invariati (nessun intervento).
		const fadeEnd = slides.length / 2 - 0.1; // opacity 0 deve avvenire entro qui
		if ( cfg.fadeStart + cfg.fadeRange > fadeEnd ) {
			cfg.fadeRange = Math.min( cfg.fadeRange, Math.max( 0.3, fadeEnd * 0.5 ) );
			cfg.fadeStart = Math.max( 0.2, fadeEnd - cfg.fadeRange );
		}
		cfg.introSpins = num( '--intro-spins', 0.85 );
		cfg.introDuration = num( '--intro-duration', 2.8 );
		// Pitch dal <li> (non trasformato → misura pulita): larghezza + margini.
		const ms = getComputedStyle( slides[ 0 ] );
		cardW = slides[ 0 ].offsetWidth;
		pitch = cardW + parseFloat( ms.marginLeft ) + parseFloat( ms.marginRight );
	}
	readVars();

	function styleByDistance() {
		if ( ! pitch ) {
			readVars();
			if ( ! pitch ) {
				return;
			}
		}
		const vp = viewport.getBoundingClientRect();
		const mid = vp.left + vp.width / 2;

		// Passata 1 — sole letture (evita layout thrash: niente set in mezzo).
		const centers = slides.map( ( s ) => {
			const r = s.getBoundingClientRect();
			return r.left + r.width / 2;
		} );

		// Offset (frazionario, continuo) della card più vicina al centro. Serve a
		// centrare la DISSOLVENZA sulla card attiva invece che sul centro geometrico
		// fisso: a metà transizione il cluster è spostato di ~mezza card e, con un
		// riferimento fisso, un lato includerebbe una card in più dell'altro (vuoto
		// laterale). Ancorando il fade a `delta` i due lati restano sempre simmetrici.
		let delta = 0;
		let dBest = Infinity;
		centers.forEach( ( c ) => {
			const o = ( c - mid ) / pitch;
			if ( Math.abs( o ) < Math.abs( dBest ) ) {
				dBest = o;
			}
		} );
		delta = dBest;

		// Direzione di scorrimento dal tempo del loop (con gestione del wrap
		// seamless). Aggiornata solo se il tempo si muove davvero, così a riposo
		// resta l'ultimo valore ma senza effetto (l'attività sotto è ~0).
		const nowT = loop.time();
		if ( lastLoopTime !== null ) {
			let dtl = nowT - lastLoopTime;
			const dur = loop.duration();
			if ( Math.abs( dtl ) > dur / 2 ) {
				dtl -= Math.sign( dtl ) * dur; // giunzione del loop infinito
			}
			if ( Math.abs( dtl ) > 1e-4 ) {
				travelDir = Math.sign( dtl );
			}
		}
		lastLoopTime = nowT;

		// Passata 2 — sole scritture.
		slides.forEach( ( s, i ) => {
			const rawOffset = ( centers[ i ] - mid ) / pitch; // dove il <li> si trova
			// Loop circolare di N card: riporta ogni card sul lato PIÙ VICINO
			// (offset ∈ [-N/2, +N/2]). Senza, horizontalLoop a riposo può accumulare
			// quasi tutte le card da un lato dell'attiva (es. attiva = ultima card),
			// lasciando l'altro lato vuoto. Il "salto" al confine ±N/2 cade dove la
			// card è già dissolta (fadeStart+fadeRange ≤ N/2), quindi resta invisibile.
			const half = slides.length / 2;
			let offset = rawOffset;
			if ( offset > half ) {
				offset -= slides.length;
			} else if ( offset < -half ) {
				offset += slides.length;
			}
			const a = Math.abs( offset );

			// Scala a tappe: centro → adiacente (a=1) → esterni (a≥2), continua.
			const scale = a <= 1
				? gsap.utils.interpolate( cfg.activeScale, cfg.neighborScale, a )
				: gsap.utils.interpolate( cfg.neighborScale, cfg.outerScale, Math.min( a - 1, 1 ) );

			// Profondità iperbolica: cresce con |offset| ma con rendimenti
			// decrescenti → le laterali si stratificano invece di collassare su un
			// piano unico. Funzione PARI in offset (la vicinanza al centro = fronte).
			// Nota: le card sono isolate (perspective per-card, track flat — vedi
			// _settore-gallery.scss), quindi `z` regola scala/scorcio della SINGOLA
			// card, mentre l'ordine di sovrapposizione tra card è dato dallo z-index
			// (painter's algorithm): niente intersezione di piani 3D → niente "split".
			const z = -cfg.depth * ( a / ( a + cfg.depthEase ) );

			// Le card laterali ruotano verso il centro; satura oltre l'adiacente.
			const rotationY = cfg.angle * gsap.utils.clamp( -1, 1, offset );

			// Spacing LINEARE: targetX = passo costante × offset → distribuzione
			// uniforme e simmetrica su tutta la larghezza (nessuna compressione né
			// accumulo ai bordi). Sottraiamo la posizione naturale del <li> per
			// ottenere il translateX del link. Essendo lineare in offset, un piccolo
			// errore di snap trasla il cluster ma NON sbilancia i due lati.
			const targetX = offset * cardW * cfg.cardStep;
			const x = targetX - rawOffset * pitch; // rawOffset = posizione fisica del <li>

			// Opacità = lieve attenuazione di profondità × dissolvenza netta oltre la
			// banda visibile. La dissolvenza è misurata da `delta` (card attiva), non
			// dal centro geometrico → i due lati mostrano lo stesso numero di card e
			// non resta spazio vuoto da un lato durante lo scorrimento.
			const aFade = Math.abs( offset - delta );
			const dim = a <= 1
				? gsap.utils.interpolate( 1, 0.94, a )
				: gsap.utils.interpolate( 0.94, 0.8, Math.min( a - 1, 1 ) );
			const fade = gsap.utils.clamp( 0, 1, 1 - ( aFade - cfg.fadeStart ) / cfg.fadeRange );
			const opacity = dim * fade;

			gsap.set( cards[ i ], {
				x,
				z,
				rotationY,
				scale,
				opacity,
			} );

			// Stacking esplicito per distanza: il centro (a=0) resta davanti.
			// IMPORTANTE: lo z-index va sul <li>, non sul link. Con la perspective
			// per-card ogni .slide è un proprio stacking context, quindi lo z-index del
			// link competerebbe solo DENTRO la sua slide (le slide si ordinerebbero per
			// DOM → la centrale finirebbe sotto). Impostandolo sul <li> le slide si
			// ordinano tra loro (painter's algorithm) e la centrale resta davanti.
			// Alta risoluzione + tie-break sul verso di scorrimento: al crossover
			// (|offset| uguale) la card ENTRANTE vince sull'uscente → passaggio netto.
			gsap.set( slides[ i ], {
				zIndex: Math.round( 1000 - a * 100 ) * 2 + ( travelDir !== 0 && Math.sign( offset ) === travelDir ? 1 : 0 ),
				// Le card invisibili non raccolgono click/hover: niente hit-target
				// fantasma ai bordi (ereditato dal link interno).
				pointerEvents: opacity < 0.12 ? 'none' : 'auto',
			} );
		} );
	}
	// Stato iniziale DETERMINISTICO: horizontalLoop, a riposo, centra la card la cui
	// label cade più vicina a time 0 — non necessariamente la 0. Forziamo il centro
	// sulla card attiva del markup (is-active = slide 0) così centro, didascalia e
	// ARIA coincidono ed evitiamo il layout "a metà" del primo paint.
	loop.toIndex( 0, { duration: 0 } );
	styleByDistance();

	// Ri-applica dopo che il layout si assesta (font/immagini lazy): al primissimo
	// paint le misure possono non essere definitive → le card si sovrappongono
	// finché il ticker non parte. Un paio di ripassate lo prevengono.
	requestAnimationFrame( styleByDistance );
	window.addEventListener( 'load', styleByDistance );

	// Il pitch dipende dalla larghezza card (responsive) → rileggi su resize.
	window.addEventListener( 'resize', () => {
		readVars();
		styleByDistance();
	} );

	// --- Rullo introduttivo (rolling gallery) --------------------------------
	// Al primo ingresso in viewport la coverflow "rulla" e decelera (power3.out)
	// per poi assestarsi centrata sulla card più vicina. Da lì resta attiva la
	// normale navigazione circolare (già infinita via horizontalLoop).
	function introRoll() {
		if ( introDone || reduce || slides.length < 2 ) {
			return;
		}
		introDone = true;
		introRunning = true;
		const dur = loop.duration();
		const wrapT = gsap.utils.wrap( 0, dur );
		const proxy = { t: loop.time() };
		introTween = gsap.to( proxy, {
			t: loop.time() + dur * cfg.introSpins, // quantità di "rullata"
			duration: cfg.introDuration,
			ease: 'power3.out',
			onUpdate() {
				loop.time( wrapT( proxy.t ) ); // il ticker ridisegna la profondità
			},
			onComplete() {
				introTween = null;
				introRunning = false;
				// Assestamento morbido sulla card più vicina + didascalia/ARIA.
				const idx = loop.closestIndex( true );
				loop.toIndex( idx, {
					duration: 0.5,
					ease: 'power2.out',
					onComplete: () => syncActive( slides[ loop.current() ], loop.current() ),
				} );
			},
		} );
	}

	// Interazione utente durante il rullo → interrompi e cedi il controllo.
	function killIntro() {
		if ( introTween ) {
			introTween.kill();
			introTween = null;
		}
		if ( introRunning ) {
			introRunning = false;
			loop.closestIndex( true );
			syncActive( slides[ loop.current() ], loop.current() );
		}
	}

	// Ticker attivo solo quando la sezione è in viewport (battery-friendly).
	if ( ! reduce ) {
		let ticking = false;
		const addTick = () => {
			if ( ! ticking ) {
				gsap.ticker.add( styleByDistance );
				ticking = true;
			}
		};
		const removeTick = () => {
			if ( ticking ) {
				gsap.ticker.remove( styleByDistance );
				ticking = false;
			}
		};
		new IntersectionObserver( ( entries ) => {
			if ( entries[ 0 ].isIntersecting ) {
				styleByDistance(); // primo frame visibile già corretto
				addTick();
				introRoll(); // rullo introduttivo al primo ingresso in viewport
			} else {
				removeTick();
			}
		}, { threshold: 0.35 } ).observe( root );
	}

	// Drag/tocco sulla traccia → interrompi subito il rullo e cedi il controllo
	// (capture: prima che Draggable prenda il press).
	track.addEventListener( 'pointerdown', killIntro, { capture: true } );

	// --- Navigazione (frecce, tastiera, wheel orizzontale) ------------------
	let navBusy = false;
	function nav( dir ) {
		killIntro(); // un comando utente interrompe il rullo introduttivo
		if ( navBusy ) {
			return;
		}
		navBusy = true;
		const vars = { duration: reduce ? 0 : 0.5, ease: 'power2.inOut' };
		dir === 'next' ? loop.next( vars ) : loop.previous( vars );
		gsap.delayedCall( reduce ? 0 : 0.35, () => {
			navBusy = false;
		} );
	}

	const prevBtn = root.querySelector( '[data-gallery-prev]' );
	const nextBtn = root.querySelector( '[data-gallery-next]' );
	prevBtn && prevBtn.addEventListener( 'click', () => nav( 'prev' ) );
	nextBtn && nextBtn.addEventListener( 'click', () => nav( 'next' ) );

	// Tastiera: la viewport è focusabile (vedi markup) → ←/→.
	if ( viewport ) {
		viewport.setAttribute( 'tabindex', '0' );
		viewport.setAttribute( 'role', 'group' );
		viewport.setAttribute( 'aria-label', 'Gallery' );
		viewport.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'ArrowLeft' ) {
				e.preventDefault();
				nav( 'prev' );
			} else if ( e.key === 'ArrowRight' ) {
				e.preventDefault();
				nav( 'next' );
			}
		} );
	}

	// Wheel/trackpad: naviga solo su intento orizzontale (no scroll-jacking).
	if ( ! reduce ) {
		Observer.create( {
			target: root.querySelector( '.settore-gallery__stage' ) || root,
			type: 'wheel',
			lockAxis: true,
			tolerance: 30,
			onRight: () => nav( 'prev' ),
			onLeft: () => nav( 'next' ),
		} );
	}

	// --- Lightbox PhotoSwipe (click su qualsiasi slide) ---------------------
	const lightbox = new PhotoSwipeLightbox( {
		gallery: track,
		children: 'a',
		pswpModule: PhotoSwipe,
		bgOpacity: 0.96,
		wheelToZoom: true,
	} );

	// Didascalia in overlay (dal data-pswp-caption dell'anchor attivo).
	lightbox.on( 'uiRegister', () => {
		lightbox.pswp.ui.registerElement( {
			name: 'settore-caption',
			order: 9,
			isButton: false,
			appendTo: 'root',
			onInit: ( el, pswp ) => {
				el.className = 'settore-pswp__caption';
				pswp.on( 'change', () => {
					const anchor = pswp.currSlide && pswp.currSlide.data.element;
					el.textContent = anchor ? ( anchor.getAttribute( 'data-pswp-caption' ) || '' ) : '';
				} );
			},
		} );
	} );

	// Alla chiusura, centra la coverflow sull'immagine vista nel lightbox.
	lightbox.on( 'close', () => {
		if ( lightbox.pswp && typeof lightbox.pswp.currIndex === 'number' ) {
			loop.toIndex( lightbox.pswp.currIndex, { duration: reduce ? 0 : 0.4 } );
		}
	} );

	lightbox.init();

	// Con la card ruotata in 3D (preserve-3d + translateZ) l'hit-testing del
	// click può cadere sul <li> contenitore invece che sul <a>: in quel caso
	// PhotoSwipe non lo intercetta, quindi lo apriamo a mano sull'indice giusto.
	track.addEventListener( 'click', ( e ) => {
		if ( e.target.closest( '.settore-gallery__link' ) ) {
			return; // il click ha raggiunto il link → PhotoSwipe apre da solo
		}
		const li = e.target.closest( '.settore-gallery__slide' );
		if ( ! li ) {
			return;
		}
		const idx = slides.indexOf( li );
		if ( idx < 0 ) {
			return;
		}
		e.preventDefault();
		lightbox.loadAndOpen( idx );
	} );
}() );
